import { lazy, Suspense } from 'react';

/** Imperative access to a mounted terminal (given to `onReady` once it is open and fitted). */
export interface TerminalHandle {
  write(data: Uint8Array | string): void;
  focus(): void;
  /** Current grid size (after fitting to the container). */
  size(): { cols: number; rows: number };
  /** Clears the screen and scrollback. */
  reset(): void;
}

export interface TerminalProps {
  /** Called once the terminal is open and fitted to its container. */
  onReady: (handle: TerminalHandle) => void;
  /** Keystrokes and pastes (xterm `onData`). */
  onData: (data: string) => void;
  /** The grid size changed (container resized; already debounced). */
  onResize: (cols: number, rows: number) => void;
  className?: string;
}

// xterm.js is large: load it (and its CSS) only when a terminal is rendered.
const XtermTerminal = lazy(() => import('./XtermTerminal'));

export function Terminal(props: TerminalProps) {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center" role="status">
          <span className="loading loading-spinner loading-md text-neutral-content" aria-label="Loading terminal" />
        </div>
      }
    >
      <XtermTerminal {...props} />
    </Suspense>
  );
}
